import { Model } from '@vuex-orm/core';
import Building from '@/models/Building';
import Buildingable from '@/models/Buildingable';
import Date from '@/models/Date';
import Image from '@/models/Image';
import Imageable from '@/models/Imageable';
import Project from '@/models/Project';
import Projectable from '@/models/Projectable';
import Shape from '@/models/Shape';
import Selector from '@/models/Selector';

export default class Illustration extends Model {
  static entity = 'illustrations';

  static state() {
    return {
      activeIllustration: false,
      currentSection: 'all',
    }
  }

  static fields() {
    return {
      id: this.uid(),
      name: this.attr(null),
      description: this.attr(null),

      hasCompass: this.boolean(false),
      compassAngle: this.attr(null),

      // Relationship IDs
      selector_id: this.attr(null),

      // Relationships
      building: this.morphToMany(
        Building,
        Buildingable,
        'building_id',
        'buildingable_id',
        'buildingable_type'
      ),
      date: this.morphOne(Date, 'dateable_id', 'dateable_type'),
      image: this.morphToMany(
        Image,
        Imageable,
        'image_id',
        'imageable_id',
        'imageable_type'
      ),
      project: this.morphToMany(
        Project,
        Projectable,
        'project_id',
        'projectable_id',
        'projectable_type'
      ),
      shapes: this.morphMany(Shape, 'shapeable_id', 'shapeable_type'),
      selector: this.belongsTo(Selector, 'selector_id'),
    };
  }

  static transformer(data) {
    const illustrations = [];
    data.data.forEach(item => {
      const illustration = {};

      illustration.id = item.id;
      illustration.name = item.title.rendered;

      if (item.acf.compass_select === 'true') {
        illustration.hasCompass = true;
        illustration.compassAngle = parseInt(item.acf.compass_angle);
      }

      if (item.building.length > 0) {
        illustration.building = [];
        item.building.forEach(building => {
          illustration.building.push({id: building});
        });
      }

      if (item.project.length > 0) {
        illustration.project = [];
        item.project.forEach(project => {
          illustration.project.push({id: project});
        });
      }

      // Add date object to Illustration object
      illustration.date = {
        date: item.date,
        date_gmt: item.date_gmt,
        modified: item.modified,
        modified_gmt: item.modified_gmt
      };

      // Check if there are any images
      if (item.acf.image) {
        const image = item.acf.image;
        // Create empty array on Illustration object.
        illustration.image = [];
        // Clean up data and add to array.
        illustration.image.push({
          id: image.id,
          filename: image.filename,
          title: image.title,
          alt: image.alt,
          caption: image.caption,
          description: image.description,
          mime_type: image.mime_type,
          type: image.type,
          subtype: image.subtype,
          url: image.url,
          width: image.width,
          height: image.height,
          date: {
            date: image.date,
            modified: image.modified,
          },
          sizes: [
            {
              name: 'thumbnail',
              url: image.sizes.thumbnail,
              width: image.sizes['thumbnail-width'],
              height: image.sizes['thumbnail-height'],
            },
            {
              name: 'medium',
              url: image.sizes.medium,
              width: image.sizes['medium-width'],
              height: image.sizes['medium-height'],
            },
            {
              name: 'medium_large',
              url: image.sizes.medium_large,
              width: image.sizes['medium_large-width'],
              height: image.sizes['medium_large-height'],
            },
            {
              name: 'large',
              url: image.sizes.large,
              width: image.sizes['large-width'],
              height: image.sizes['large-height'],
            },
            {
              name: '1536x1536',
              url: image.sizes['1536x1536'],
              width: image.sizes['1536x1536-width'],
              height: image.sizes['1536x1536-height'],
            },
            {
              name: '2048x2048',
              url: image.sizes['2048x2048'],
              width: image.sizes['2048x2048-width'],
              height: image.sizes['2048x2048-height'],
            },
          ],
        })
      }


      illustrations.push(illustration);
    });
    return illustrations;
  }

  static apiConfig = {
    dataTransformer: (data) => this.transformer(data),
  };

  static fetch(project, baseURL) {
    // TODO: Implement paginated fetch
    let url = '/wp-json/wp/v2/illustration?order=asc&per_page=100';
    if (project) {
      url = `${url}&project=${project}`;
    }
    return this.api().get(url, {
      baseURL: baseURL,
    })
    ;
  }

  static getSection(section, selector) {
    if (section === 'all') {
      return Illustration.findIn(selector.overview_illustration_ids);
    } else {
      return Illustration.findIn(selector.building_illustration_ids[section]);
    }
  }

  static getCurrentIllustration(id) {
    return Illustration.query()
      .whereId(id)
      .get()
  }
}
